(function () {
  'use strict';

  const root = document.getElementById('TpSearchDrawerRoot');
  if (!root) return;

  const drawer = document.getElementById('TpSearchDrawer');
  const overlay = root.querySelector('.tp-search-drawer__overlay');
  const input = root.querySelector('[data-search-input]');
  const resultsEl = root.querySelector('[data-search-results]');

  const STRINGS = {
    loading: root.getAttribute('data-loading-text') || '',
    noResults: root.getAttribute('data-no-results-text') || '',
    viewAll: root.getAttribute('data-view-all-text') || '',
  };
  const searchUrl = root.getAttribute('data-search-url') || '/search';

  const MIN_CHARS = 2;
  const DEBOUNCE_MS = 300;
  const LIMIT = 6;

  let lastFocused = null;
  let debounceId = null;
  let controller = null;

  /* -------- Helpers (mirrors custom-cart-drawer.js) -------- */

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function sizedImg(url, size) {
    if (!url) return '';
    const sep = url.indexOf('?') === -1 ? '?' : '&';
    return url + sep + 'width=' + size;
  }

  // Predictive search returns money as decimal strings in major units (e.g. "19.99").
  function formatPrice(value) {
    const n = parseFloat(value);
    if (isNaN(n)) return '';
    return n.toFixed(2).replace('.', ',') + ' €';
  }

  /* -------- Rendering -------- */

  function renderCard(product) {
    const imgUrl =
      (product.featured_image && product.featured_image.url) || product.image || '';
    const thumb = imgUrl
      ? `<img src="${escapeHtml(sizedImg(imgUrl, 160))}" srcset="${escapeHtml(
          sizedImg(imgUrl, 160)
        )} 1x, ${escapeHtml(
          sizedImg(imgUrl, 320)
        )} 2x" width="80" height="80" alt="${escapeHtml(product.title)}" loading="lazy">`
      : '';

    const price = product.price_min != null ? product.price_min : product.price;
    const compare =
      product.compare_at_price_min != null
        ? product.compare_at_price_min
        : product.compare_at_price;
    const onSale = parseFloat(compare) > parseFloat(price) && parseFloat(compare) > 0;

    const priceInner = onSale
      ? `<span class="tp-search-drawer__card-price-compare">${formatPrice(compare)}</span>
         <span class="tp-search-drawer__card-price-sale">${formatPrice(price)}</span>`
      : `${formatPrice(price)}`;

    return `
      <a class="tp-search-drawer__card" href="${escapeHtml(product.url)}">
        <span class="tp-search-drawer__card-thumb">${thumb}</span>
        <span class="tp-search-drawer__card-info">
          <span class="tp-search-drawer__card-title">${escapeHtml(product.title)}</span>
          <span class="tp-search-drawer__card-price">${priceInner}</span>
        </span>
      </a>`;
  }

  function showLoading() {
    resultsEl.innerHTML = `
      <div class="tp-search-drawer__state tp-search-drawer__loading">
        <span class="tp-search-drawer__spinner" aria-hidden="true"></span>
        <span>${escapeHtml(STRINGS.loading)}</span>
      </div>`;
  }

  function showNoResults(query) {
    const msg = STRINGS.noResults.replace('{query}', escapeHtml(query));
    resultsEl.innerHTML = `<div class="tp-search-drawer__state tp-search-drawer__empty">${msg}</div>`;
  }

  function showResults(products, query) {
    const cards = products.slice(0, LIMIT).map(renderCard).join('');
    const viewAllHref = searchUrl + '?q=' + encodeURIComponent(query) + '&type=product';
    resultsEl.innerHTML = `
      <div class="tp-search-drawer__results-grid">${cards}</div>
      <a class="tp-search-drawer__view-all" href="${escapeHtml(viewAllHref)}">${escapeHtml(
      STRINGS.viewAll
    )}</a>`;
  }

  function clearResults() {
    resultsEl.innerHTML = '';
  }

  /* -------- Predictive search fetch -------- */

  async function runSearch(query) {
    if (controller) controller.abort();
    controller = new AbortController();

    const url =
      '/search/suggest.json?q=' +
      encodeURIComponent(query) +
      '&resources[type]=product&resources[limit]=' +
      LIMIT;

    showLoading();

    try {
      const res = await fetch(url, {
        signal: controller.signal,
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error('search_failed');
      const data = await res.json();
      const products =
        (data.resources && data.resources.results && data.resources.results.products) || [];

      // Ignore if the input changed while this request was in flight.
      if (input.value.trim() !== query) return;

      if (products.length === 0) {
        showNoResults(query);
      } else {
        showResults(products, query);
      }
    } catch (e) {
      if (e.name === 'AbortError') return; // superseded by a newer keystroke
      showNoResults(query);
    }
  }

  function onInput() {
    const query = input.value.trim();
    if (debounceId) clearTimeout(debounceId);

    if (query.length < MIN_CHARS) {
      if (controller) controller.abort();
      clearResults();
      return;
    }

    debounceId = setTimeout(function () {
      runSearch(query);
    }, DEBOUNCE_MS);
  }

  /* -------- Open / close -------- */

  function openDrawer() {
    if (root.classList.contains('is-open')) return;
    lastFocused = document.activeElement;
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    // Focus the input immediately so typing starts right away.
    if (input) input.focus();
  }

  function closeDrawer() {
    if (!root.classList.contains('is-open')) return;
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    if (controller) controller.abort();
    if (debounceId) clearTimeout(debounceId);
    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
  }

  /* -------- Event wiring -------- */

  // Open from the header search icon (capture phase, keeps href as no-JS fallback).
  document.addEventListener(
    'click',
    function (e) {
      const trigger = e.target.closest('.header-action-btn--search, [data-search-open]');
      if (!trigger) return;
      e.preventDefault();
      e.stopPropagation();
      openDrawer();
    },
    true
  );

  // Close triggers: X button + overlay.
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-search-close]')) {
      closeDrawer();
    }
  });

  if (overlay) {
    overlay.addEventListener('click', function (e) {
      e.stopPropagation();
      closeDrawer();
    });
  }

  // Escape closes the drawer (focus returns to the search icon via lastFocused).
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && root.classList.contains('is-open')) {
      e.preventDefault();
      closeDrawer();
    }
  });

  if (input) {
    input.addEventListener('input', onInput);
  }

  // Enter submits the form → native navigation to /search?q=… (full results page).
})();
